import "@cloudscape-design/global-styles/index.css";
import "./App.css";
import { useState } from "react";
import { AppLayout, ContentLayout, Header, SideNavigation, TopNavigation } from "@cloudscape-design/components";
import { applyMode, Mode } from "@cloudscape-design/global-styles";
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import ProjectsPage from "./ProjectsPage";
import ProjectDetailPage from "./ProjectDetailPage";
import StackDetailPage from "./StackDetailPage";
import ProductDetailPage from "./ProductDetailPage";
import AccountsPage from "./AccountsPage";
import InventoryPage from "./InventoryPage";
import InventoryDetailPage from "./InventoryDetailPage";
import LiveInventoryPage from "./LiveInventoryPage";
import LiveEc2Page from "./LiveEc2Page";
import LiveEc2DetailPage from "./LiveEc2DetailPage";
import LiveEcsPage from "./LiveEcsPage";
import LiveEcsDetailPage from "./LiveEcsDetailPage";
import LiveLambdaPage from "./LiveLambdaPage";
import LiveLambdaDetailPage from "./LiveLambdaDetailPage";
import LiveSqsPage from "./LiveSqsPage";

import LiveFmSdipPage from "./LiveFmSdipPage";
import StakeholdersPage from "./StakeholdersPage";
import StakeholderGroupsPage from "./StakeholderGroupsPage";
import SettingsPage from "./SettingsPage";
import CreateProductLandingPage from "./CreateProductLandingPage";
import CreateProductPage from "./CreateProductPage";
import CreateFmBundlePage from "./CreateFmBundlePage";
import EditProductPage from "./EditProductPage";
import SapDataspherePage from "./SapDataspherePage";
import KiroAccessPage from "./KiroAccessPage";
import RequestKiroAccessPage from "./RequestKiroAccessPage";
import SyncByIclProjectPage from "./SyncByIclProjectPage";
import SyncAllByIclProjectPage from "./SyncAllByIclProjectPage";
import DynamicPage from "./DynamicPage";
import PagesListPage from "./PagesListPage";
import PageEditorPage from "./PageEditorPage";

  /**
   * Legacy FM bundle detail URL (/projects/:id/fm-bundles/:bundleId) now that FM bundles are a
   * stackless product type — redirect to the product detail route. `replace` so the old URL isn't
   * left in history.
   */
function FmBundleRedirect() {
    const { bundleId } = useParams<{ bundleId: string }>();
    return <Navigate to={`/products/fm_bundle/${bundleId}`} replace />;
  }

function App({ onLogout, ssoEnabled }: { onLogout?: () => void; ssoEnabled?: boolean }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(false);

  const handleDarkModeToggle = (checked: boolean) => {
    setDarkMode(checked);
    applyMode(checked ? Mode.Dark : Mode.Light);
  };

  return (
    <>
      <div id="top-nav">
        <TopNavigation
          identity={{
            title: "IT Project Management",
            href: "/projects",
            onFollow: () => navigate("/projects"),
          }}
          utilities={[
            {
              type: "button",
              iconName: "settings",
              text: darkMode ? "Light mode" : "Dark mode",
              onClick: () => handleDarkModeToggle(!darkMode),
            },
            ...(onLogout
              ? [
                  {
                    type: "button" as const,
                    iconName: "user-profile" as const,
                    text: "Logout",
                    onClick: onLogout,
                  },
                ]
              : []),
          ]}
        />
      </div>
      <AppLayout
        headerSelector="#top-nav"
        toolsHide
        contentType="table"
        navigation={
        <SideNavigation
          activeHref={pathname}
          onFollow={(e) => { e.preventDefault(); navigate(e.detail.href); }}
          header={{
            text: "IT Project Management",
            href: "/projects",
          }}
          items={[
            { type: "link", text: "Projects", href: "/projects" },
            { type: "link", text: "Inventory", href: "/inventory" },
            { type: "divider" },
            { type: "section", text: "Live Inventory", items: [
              { type: "link", text: "Overview", href: "/live-inventory" },
              { type: "link", text: "EC2", href: "/live-inventory/ec2" },
              { type: "link", text: "Lambda", href: "/live-inventory/lambda" },
              { type: "link", text: "API Gateway", href: "/live-inventory/api-gateway" },
              { type: "link", text: "ECS", href: "/live-inventory/ecs" },
              { type: "link", text: "RDS", href: "/live-inventory/rds" },
              { type: "link", text: "DynamoDB", href: "/live-inventory/dynamodb" },
              { type: "link", text: "SQS", href: "/live-inventory/sqs" },


              { type: "link", text: "FM SDIPs", href: "/live-inventory/fm-sdips" },
            ]},
            { type: "divider" },
            { type: "link", text: "Create Product", href: "/create" },
            { type: "link", text: "Kiro Access", href: "/kiro-access" },
            { type: "divider" },
            { type: "section", text: "Stakeholders", items: [
              { type: "link", text: "Stakeholders", href: "/stakeholders" },
              { type: "link", text: "Stakeholder Groups", href: "/stakeholder-groups" },
            ]},
            { type: "divider" },
            { type: "section", text: "Integrations", items: [
              { type: "link", text: "Accounts", href: "/accounts" },
              { type: "link", text: "SAP Datasphere", href: "/sap-datasphere" },
            ]},
            { type: "divider" },
            { type: "section", text: "Admin", items: [
              { type: "section", text: "Sync", items: [
                { type: "link", text: "Sync By ICLProject", href: "/admin/sync/by-iclproject" },
                { type: "link", text: "Sync All By ICLProject", href: "/admin/sync/all-by-iclproject" },
              ]},
            ]},
            { type: "divider" },
            { type: "link", text: "Settings", href: "/settings" },
          ]}
        />
      }
      content={
        <ContentLayout
          header={
            <Header variant="h1" description="Manage projects, infrastructure, and stakeholders across your AWS accounts.">
              IT Project Management
            </Header>
          }
        >
          <Routes>
            <Route path="/projects/:id" element={<ProjectDetailPage />} />
            <Route path="/projects/:id/fm-bundles/:bundleId" element={<FmBundleRedirect />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/projects/:projectId/stacks/:stackId/edit" element={<EditProductPage />} />
            <Route path="/stacks/:id" element={<StackDetailPage />} />
            <Route path="/products/:type/:id" element={<ProductDetailPage />} />
            <Route path="/accounts" element={<AccountsPage />} />
            <Route path="/stakeholders" element={<StakeholdersPage />} />
            <Route path="/stakeholder-groups" element={<StakeholderGroupsPage />} />
            <Route path="/inventory/:assetType/:id" element={<InventoryDetailPage />} />
            <Route path="/inventory" element={<InventoryPage />} />
            <Route path="/live-inventory/ec2/:instanceId" element={<LiveEc2DetailPage />} />
            <Route path="/live-inventory/ec2" element={<LiveEc2Page />} />
            <Route path="/live-inventory/ecs/:clusterName/:serviceName" element={<LiveEcsDetailPage />} />
            <Route path="/live-inventory/ecs" element={<LiveEcsPage />} />
            <Route path="/live-inventory/lambda/:functionName" element={<LiveLambdaDetailPage />} />
            <Route path="/live-inventory/lambda" element={<LiveLambdaPage />} />
            <Route path="/live-inventory/sqs" element={<LiveSqsPage />} />

           <Route path="/live-inventory/fm-sdips" element={<LiveFmSdipPage />} />
            <Route path="/live-inventory" element={<LiveInventoryPage />} />
            <Route path="/settings" element={<SettingsPage ssoEnabled={ssoEnabled} />} />
            <Route path="/create" element={<CreateProductLandingPage />} />
            <Route path="/create/fm-bundle" element={<CreateFmBundlePage />} />
            <Route path="/create/:productType" element={<CreateProductPage />} />
            <Route path="/sap-datasphere" element={<SapDataspherePage />} />
            <Route path="/kiro-access/request" element={<RequestKiroAccessPage />} />
            <Route path="/kiro-access" element={<KiroAccessPage />} />
            <Route path="/admin/sync/by-iclproject" element={<SyncByIclProjectPage />} />
            <Route path="/admin/sync/all-by-iclproject" element={<SyncAllByIclProjectPage />} />
            <Route path="/pages/new" element={<PageEditorPage />} />
            <Route path="/pages/:id" element={<PageEditorPage />} />
            <Route path="/pages" element={<PagesListPage />} />
            <Route path="/p/:slug" element={<DynamicPage />} />            
            <Route path="*" element={<Navigate to="/projects" replace />} />
          </Routes>
        </ContentLayout>
      }
    />
    </>
  );
}

export default App;
